import { apiFetch, apiFetchWithRetry } from '@/lib/apiFetch';
class UnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnauthorizedError";
  }
}

export function handleUnauthorized(): never {
  sessionStorage.removeItem('access_token');
  localStorage.removeItem('role');
  localStorage.removeItem('flow');
  window.location.href = '/login';
  throw new UnauthorizedError("Session expired. Please log in again.");
}

class NotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotFoundError";
  }
}

export class AccountDeactivatedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AccountDeactivatedError";
  }
}

export type Role = "TEACHER" | "STUDENT";

export interface UserRoleDto {
  id?: string;
  userId?: string;
  role: Role;
  isDefault: boolean;
  /** When this role was added — the backend already sends this; it just wasn't typed here yet. */
  createdDate?: string;
}

export interface UserProfile {
  id: string;
  email: string;
  name?: string | null;
  phoneNumber?: string | null;
  cognitoId: string;
  roles: UserRoleDto[];
  defaultRole: Role | null;
  goalOnboardingCompleted: boolean;
  dateOfBirth?: string | null;
  isMinor?: boolean;
}

export const getProfile = async (accessToken: string): Promise<UserProfile> => {
  const response = await apiFetchWithRetry('/api/userManagement/v1/me', {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken}`
    }
  });

  if (response.status === 401) {
    handleUnauthorized();
  }
  if (response.status === 404) {
    throw new NotFoundError("User profile not found");
  }
  if (response.status === 403) {
    const body = await response.json().catch(() => null);
    if (body?.errorCode === "ACCOUNT_DEACTIVATED") {
      throw new AccountDeactivatedError("Account deactivated");
    }
  }
  if (!response.ok) {
    throw new Error("Failed to fetch profile");
  }
  return response.json() as Promise<UserProfile>;
};

export const setDefaultRole = async (
  accessToken: string,
  role: Role
): Promise<UserProfile> => {
  const response = await apiFetch('/api/userManagement/v1/me/role', {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken}`
    },
    body: JSON.stringify({ role })
  });

  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    throw new Error("Failed to set role");
  }
  return response.json() as Promise<UserProfile>;
};

export const switchDefaultRole = async (
  accessToken: string,
  role: Role
): Promise<UserProfile> => {
  const response = await apiFetch('/api/userManagement/v1/me/default-role', {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken}`
    },
    body: JSON.stringify({ role })
  });

  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    throw new Error("Failed to switch role");
  }
  return response.json() as Promise<UserProfile>;
};

export interface UpdateUserProfileRequest {
  name?: string;
  phone?: string;
  email?: string;
  profilepicture?: string;
  /** ISO country code; saved to the student and/or teacher profile. 400 on unknown/inactive code. */
  countryCode?: string;
}

export const updateUserProfile = async (
  accessToken: string,
  patch: UpdateUserProfileRequest
): Promise<UserProfile> => {
  const response = await apiFetch('/api/userManagement/v1/users/profile', {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken}`
    },
    body: JSON.stringify(patch)
  });

  if (response.status === 401) {
    handleUnauthorized();
  }
  if (!response.ok) {
    // Surface the server's message (e.g. 400 for an unknown/inactive countryCode).
    let errorMessage = "Failed to update profile";
    try {
      const errorText = await response.text();
      const errorJson: { message?: string; error?: string } = JSON.parse(errorText);
      errorMessage = errorJson.message || errorJson.error || errorMessage;
    } catch { /* empty or non-JSON body — keep the generic message */ }
    throw new Error(errorMessage);
  }
  return response.json() as Promise<UserProfile>;
};

export interface AnonymousContextPayload {
  intent: 'CONTACT' | 'BOOK' | 'NOTIFY_ME';
  teacherSlug: string;
  offeringId?: string;
  searchContext?: Record<string, unknown>;
  sourceUrl: string;
}

export const linkAnonymousContext = async (
  accessToken: string,
  payload: AnonymousContextPayload
): Promise<void> => {
  const response = await apiFetch('/api/userManagement/v1/me/link-anonymous-context', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) { handleUnauthorized(); }
  if (!response.ok) {
    console.warn('linkAnonymousContext: failed silently', response.status);
  }
};

export { UnauthorizedError, NotFoundError };
